import { FC } from 'react'
import { useNavigate } from 'react-router'
import base64 from 'base-64'

import { useReapitConnect } from '@reapit/connect-session'
import { useSnack } from '@reapit/elements'

import { reapitConnectBrowserSession } from '@reapit-sales/reapit-connect'
import { Navigation, NavigationMenu, SecondaryNavigationMenu, useModalIframe } from '@reapit-sales/navigation'
import {
  LegacyCRMIframeTriggerClickEvent,
  PanelLegacyLink,
  ReactToLegacyCrmPostMessageAction,
  useLegacyIframesVisibilityContext,
  useLegacyCRMUrl,
} from '@reapit-sales/legacy-crm'
import {
  AccessLevel,
  Permission,
  Privileges,
  Settings,
  SmsCreditsMenu,
  StaffMember,
  useUserConfigurationsContext,
  useGetConfigurations,
  setClientID,
} from '@reapit-sales/api-v2'
import { currentTabOrganisationId, useOrganisationsContext, usePutUserLastSelectedOrg } from '@reapit-sales/api-v3'

import { OfficeMenu } from '@reapit-sales/ui-office-switcher'

import { NAV_MENU, NavTitle } from '@crm/core/constants'
import { buildPropertiesPageURL } from '@crm/components/properties/buildPropertiesPageURL'
import { buildContactsPageURL } from '@crm/components/contacts/buildContactsPageURL'
import { buildLeadsPageURL } from '@crm/components/leads/buildLeadsPageURL'
import { buildReportsPageURL } from '@crm/components/reports/buildReportsPageURL'
import { buildProspectorPageURL } from '@crm/components/prospector/buildProspectorPageURL'
import { buildTrustPageURL } from '@crm/components/trust/buildTrustPageURL'
import { getHelpMenu, getMyOfficesMenu, getQuickAdd, getTaskMenu } from './navigation-options'
import { getUserMenu } from './navigation-options/getUserMenu'
import { getUniversalSearchTrigger } from './navigation-options/getUniversalSearchTrigger'
import { useOfficeMenu } from './navigation-options/useOfficeMenu'
import { trackMixpanelAction, getAvatarInitials, NAVIGATION, appSwitcher, featureToggle } from '@reapit-sales/utilities'
import { getMainLogoMenu } from './navigation-options/getMainLogoMenu'
import { useLogoutHandler } from '@crm/components/LogoutManager'

export const Nav: FC = () => {
  const navigate = useNavigate()
  const { connectSession } = useReapitConnect(reapitConnectBrowserSession)
  const { isLegacyCRMIframeLoaded, legacyCRMIframePageName } = useLegacyIframesVisibilityContext()
  const { handleLogoutClick, renderLogoutDialog } = useLogoutHandler()
  const modalController = useModalIframe()
  const officeSwitcherMenu = useOfficeMenu()
  const userContext = useUserConfigurationsContext()
  const { mutate: putLastSelectedOrg } = usePutUserLastSelectedOrg()
  const legacyCRMUrl = useLegacyCRMUrl()
  const { refetch: getConfiguration } = useGetConfigurations({ enabled: false })
  const { selectedOrganisation, setSelectedOrganisation } = useOrganisationsContext()
  const { error } = useSnack()

  const userConfig: {
    privileges: Privileges
    settings: Settings
    staffMember: StaffMember
    accessLevel: AccessLevel
    teams: any[]
    smsCreditsMenu: SmsCreditsMenu
    permissions: Permission[]
  } = {
    privileges: userContext.privileges!,
    settings: userContext.settings!,
    staffMember: userContext.staffMember!,
    accessLevel: userContext.accessLevel!,
    teams: userContext.teams!,
    smsCreditsMenu: userContext.smsCreditsMenu!,
    permissions: userContext.permissions!,
  }

  const { privileges, settings, staffMember, smsCreditsMenu } = userConfig

  // Check if AppSwitcher flag is enabled
  const isAppSwitcherEnabled = featureToggle(appSwitcher, settings)

  // Default to the first office if no selection
  const selectedOfficeId: string = selectedOrganisation?.id || officeSwitcherMenu[0].id

  const mainLogoOption: NavigationMenu = getMainLogoMenu(privileges, navigate, legacyCRMIframePageName)

  const navOptions: NavigationMenu[] = [
    {
      itemIndex: 2,
      title: NAV_MENU.PROPERTIES as NavTitle,
      hasAccess: privileges.properties?.canAccess,
      onClick: () => {
        // Track the Mixpanel event for navigating to Properties
        trackMixpanelAction(NAVIGATION.ACTION.NAVIGATE_PROPERTIES, {
          DestinationRoute: buildPropertiesPageURL(),
          FromRoute: legacyCRMIframePageName ?? '',
        })
      },
      href: buildPropertiesPageURL(),
    },
    {
      itemIndex: 3,
      title: NAV_MENU.CONTACTS as NavTitle,
      hasAccess: privileges.contacts?.canAccess,
      onClick: () => {
        // Track the Mixpanel event for navigating to Contacts
        trackMixpanelAction(NAVIGATION.ACTION.NAVIGATE_CONTACTS, {
          DestinationRoute: buildContactsPageURL(),
          FromRoute: legacyCRMIframePageName ?? '',
        })
      },
      href: buildContactsPageURL(),
    },
    {
      itemIndex: 4,
      title: NAV_MENU.LEADS as NavTitle,
      hasAccess: privileges.leadCapture?.canAccess,
      onClick: () => {
        // Track the Mixpanel event for navigating to Leads
        trackMixpanelAction(NAVIGATION.ACTION.NAVIGATE_LEADS, {
          DestinationRoute: buildLeadsPageURL(),
          FromRoute: legacyCRMIframePageName ?? '',
        })
      },
      href: buildLeadsPageURL(),
    },
    {
      itemIndex: 5,
      title: NAV_MENU.REPORTS as NavTitle,
      hasAccess: privileges.reports?.canAccess,
      onClick: () => {
        // Track the Mixpanel event for navigating to Reports
        trackMixpanelAction(NAVIGATION.ACTION.NAVIGATE_REPORTS, {
          DestinationRoute: buildReportsPageURL(),
          FromRoute: legacyCRMIframePageName ?? '',
        })
      },
      href: buildReportsPageURL(),
    },
    {
      itemIndex: 6,
      title: NAV_MENU.PROSPECTOR as NavTitle,
      hasAccess: privileges.prospector?.canAccess,
      onClick: () => {
        // Track the Mixpanel event for navigating to Prospector
        trackMixpanelAction(NAVIGATION.ACTION.NAVIGATE_PROSPECTOR, {
          DestinationRoute: buildProspectorPageURL(),
          FromRoute: legacyCRMIframePageName ?? '',
        })
      },
      href: buildProspectorPageURL(),
    },
    {
      itemIndex: 7,
      title: NAV_MENU.TRUST as NavTitle,
      hasAccess: privileges.salesTrust?.canAccess,
      onClick: () => {
        // Track the Mixpanel event for navigating to Trust
        trackMixpanelAction(NAVIGATION.ACTION.NAVIGATE_TRUST, {
          DestinationRoute: buildTrustPageURL(),
          FromRoute: legacyCRMIframePageName ?? '',
        })
      },
      href: buildTrustPageURL(),
    },
  ]

  const secondaryNavOptions: SecondaryNavigationMenu[] = [
    getHelpMenu(userConfig, modalController, legacyCRMIframePageName, settings),
    ...(!settings?.system?.isLite
      ? [getQuickAdd(privileges, isLegacyCRMIframeLoaded, legacyCRMIframePageName, legacyCRMUrl)]
      : []),
    getTaskMenu(privileges, navigate, legacyCRMIframePageName),
    getMyOfficesMenu(navigate, legacyCRMIframePageName),
  ]

  const userMenuOptions = {
    userInitials: getAvatarInitials(connectSession),
    subMenu: getUserMenu(
      isLegacyCRMIframeLoaded,
      legacyCRMIframePageName,
      privileges,
      settings,
      staffMember,
      smsCreditsMenu,
      legacyCRMUrl,
      navigate,
      handleLogoutClick,
    ),
    onClick: () => {
      // Track the Mixpanel event for opening User Menu
      trackMixpanelAction(NAVIGATION.ACTION.OPEN_USER_MENU, {
        Menu: NAVIGATION.MENU_NAME.USER_MENU,
        FromRoute: legacyCRMIframePageName,
      })
    },
  }

  const universalSearchTrigger = getUniversalSearchTrigger(
    isLegacyCRMIframeLoaded,
    legacyCRMUrl,
    legacyCRMIframePageName,
  )

  const handleOfficeChange = async (selectedOffice: OfficeMenu) => {
    if (!selectedOffice.url) {
      error('The office data could not be retrieved. Please try again or contact support.')
      return
    }

    // Set the selected office in the context
    // This will update the selected organisation in the context
    // and trigger any necessary re-renders in components that depend on it
    await setSelectedOrganisation(selectedOffice)

    // Store the selected office ID in session storage for the current tab
    // This is to ensure that the current tab retains the selected office ID
    // This is particularly useful in multi-tab scenarios where the user might switch tabs,
    // and we want to ensure the last selected office is set correctly for each tab.
    await sessionStorage.setItem(currentTabOrganisationId, selectedOffice.id)

    // Update the last selected organisation in the backend
    // This is to ensure that the last selected organisation is stored in the backend
    // and can be retrieved in future sessions
    await putLastSelectedOrg({ id: selectedOffice.id })

    const xClientID = base64.encode(selectedOffice.url || '')
    // Set the client ID in the context
    // This is to ensure that the client ID is set correctly for the current office
    // and can be used in API headers as X-Client-ID
    await setClientID(xClientID)

    // This is to ensure that the latest configuration is fetched for the selected office
    // and can be used in components that depend on it
    await getConfiguration()

    // Navigate the user to the index route after an office change.
    // Currently, the index route is either "dashboard" or "my-office".
    // The logic is implemented dynamically, see `apps/crm/src/core/utils/usePrivilegeCheck.ts`.
    await navigate('/')
  }

  // To do, Active state for the menu option
  return (
    // To remove: Remove div container with the Click event when react universal search is played
    // Below click - To close universal search when any element of topbar is clicked
    <div
      id="top-bar-navigation"
      onClick={() =>
        LegacyCRMIframeTriggerClickEvent({
          action: ReactToLegacyCrmPostMessageAction.ReactToIframeMessageTriggerClickEvent,
          iframeLoaded: isLegacyCRMIframeLoaded,
          idSelector: PanelLegacyLink.ReactTopBar,
          legacyCRMUrl,
        })
      }
    >
      <Navigation
        isAppSwitcherEnabled={isAppSwitcherEnabled}
        mainLogo={mainLogoOption}
        menu={navOptions}
        secondaryMenu={secondaryNavOptions}
        userMenu={userMenuOptions}
        modalController={modalController}
        universalSearch={universalSearchTrigger}
        officeMenu={officeSwitcherMenu}
        selectedOfficeId={selectedOfficeId}
        onOfficeChange={handleOfficeChange}
      />
      {/* Below Logout Dialog appears conditionally, check useLogoutHandler hook  */}
      {renderLogoutDialog()}
    </div>
  )
}

export default Nav
